// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface NavigationItem<P = Record<string, any>> {
    route: string;
    url: string;
    params?: P;
}

export enum RouteGuardContext {
    Transition = 'route-transition',
    Unload = 'before-unload',
}

/**
 * A guard that may be executed in a blocking manner.
 *
 * Because of being limited to synchronous operations,
 * these guards can be executed either in all contexts, including during a `beforeunload` event.
 */
export type ProceduralGuard = (context: RouteGuardContext) => boolean;

/**
 * An asynchronous guard that must wait (e.g., on user interaction, to make API request)
 * to determine if a transition can occur. This is useful for displaying a dialog to
 * prompt users.
 *
 * These guards are non-blocking and cannot be waited on during a `beforeunload` event, so
 * they are limited only to route transitions.
 */
export type InteractiveGuard = () => Promise<boolean>;

type RequiredNavItem<P> = Required<NavigationItem<P>>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class HistoryEntry<P = Record<string, any>> implements RequiredNavItem<P> {
    constructor(
        navItem: Readonly<NavigationItem<P>>,
        public readonly event?: Event
    ) {
        const { route, url, params } = navItem;
        this.route = route;
        this.url = url;
        this.params = params || ({} as P);
    }

    public readonly route: RequiredNavItem<P>['route'];

    public readonly url: RequiredNavItem<P>['url'];

    public readonly params: RequiredNavItem<P>['params'];

    public readonly timestamp = new Date();

    /**
     * Compares two history items for equality.
     * @returns `true` if `other` is equivalent to this history item.
     */
    public compare(other: HistoryEntry<P>) {
        return this.route === other.route && this.url === other.url;
    }
}
